import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    CircularProgress,
    DropdownItem,
    Dropdown,
    INLINE_VARIANT,
    makeStyles,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    TextField
} from '@ellucian/react-design-system/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '../../SnackbarContext/SnackbarContext';
import {
    spacing30
} from '@ellucian/react-design-system/core/styles/tokens';
import { useApi } from '../../ApiContext/AdminApiContext';
import RichTextField from '../../Admin/AdminControls/RichTextField';
import { CONSTANTS, richTextEditorEmailSendPlaceholders } from '../../Helpers/Configuration';
import validateEmail from '../../../utilities/validateEmail';
const useStyles = makeStyles((/* theme */) => ({
    mainContent: {
        paddingTop: spacing30
    },
    contentPadding: {
        paddingTop: spacing30,
    },
    textEditor: {
        '& * .ql-editor': {
            minHeight: '150px'
        }
    },
    alert: {
        padding: '0.7rem !important',
        marginBotton: '0.5rem !important'
    },
    loading: {
        textAlign: 'center',
        marginTop: '5rem',
    }
}));


const SendEmailDialog = props => {
    const { isEmailDialogOpen, onClose, personalization, fullScreen = false } = props;

    const snackbar = useSnackbar();
    const intl = useIntl();
    const classes = useStyles();
    const api = useApi();

    const [verifiedEmails, setVerifiedEmails] = useState([{ emailAddress: '' }]);
    const [emailSender, setEmailSender] = useState('');
    const [emailRecipient, setEmailRecipient] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailText, setEmailText] = useState('');
    const [emailSendError, setEmailSendError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState({ elcnEmailSender: false, elcnEmailRecipient: false, elcnEmailSubject: false, elcnEmailText: false });

    useEffect(() => {
        const fn = async () => {

            await fetchAllEmailVerifications();
            if (personalization.length > 0) {
                let email = personalization.find(x => x.dataId === 'email')?.value;

                if (email) {
                    const error = validateEmail(email);
                    if (error) {
                        setError({ ...error, ['elcnEmailRecipient']: true });
                        setEmailRecipient('');
                    }
                    else {
                        setEmailRecipient(email);
                    }
                }
            }
        };
        fn();
    }, [personalization]);

    const fetchAllEmailVerifications = useCallback(async () => {
        try {
            const { data } = await api.communicate.getVerifiedEmails();
            if (!data) {
                snackbar.error(intl.formatMessage({ id: 'emailValidationFailedToFetch' }));
                return;
            }
            setVerifiedEmails(data.externalVerifiedEmailAddresses);
        } catch (error) {
            console.log(error);
            snackbar.error(intl.formatMessage({ id: 'emailValidationFailedToFetch' }));
        }
    }, []);

    const updateEmailSender = useCallback((value) => {
        setEmailSender(value);
        setError({ ...error, ['elcnEmailSender']: false });
    }, [error]);

    const updateEmailRecipient = useCallback((value) => {
        setEmailRecipient(value);
        setError({ ...error, ['elcnEmailRecipient']: false });
    }, [error]);

    const updateEmailText = useCallback((value) => {
        let el = document.createElement('html');
        el.innerHTML = value;
        let tag = el.getElementsByClassName('ql-placeholder-content');

        // do we have a placeholder tag?
        if (tag.length > 0) {

            //replace span with data
            let fieldId = tag[0].dataset.id;
            let fieldValue = personalization?.find(x => x.dataId === fieldId)?.value;
            if (fieldValue) {
                value = value.replace(tag[0].outerHTML, fieldValue);
            }
            else {
                value = value.replace(tag[0].outerHTML, '');
            }
        }

        setEmailText(value);
        setError({ ...error, ['elcnEmailText']: false });
    }, [personalization, error]);
    const updateEmailSubject = useCallback((value) => {
        setEmailSubject(value);
        setError({ ...error, ['elcnEmailSubject']: false });
    }, [error]);

    const handleSendEmail = useCallback(async () => {

        if (isLoading) {
            return; //prevent double clicking
        }
        try {

            //validate input
            if (emailSender.length === 0 ||
                emailRecipient.length === 0 ||
                emailSubject.length === 0 ||
                emailText.length === 0 || emailText === '<p><br></p>') {
                setError({
                    'elcnEmailSender': emailSender.length === 0,
                    'elcnEmailRecipient': emailRecipient.length === 0,
                    'elcnEmailSubject': emailSubject.length === 0,
                    'elcnEmailText': emailText.length === 0 || emailText === '<p><br></p>' // the editor sets this to be the default content
                });
                return;
            }
            else {

                setIsLoading(true);
                const response = await api.communicate.sendEmail(emailSender, emailRecipient, emailSubject, emailText);
                setIsLoading(false);
                if (response && response.sendStatus && response.sendStatus !== CONSTANTS.communicateEmailSendStatus.failed) {
                    setEmailSender('');
                    setEmailSubject('');
                    setEmailText('');
                    onClose();
                    snackbar.success(<FormattedMessage id="applicationProcessingEmailSent" />);
                }
                else {
                    setEmailSendError(true);
                }
            }
        }
        catch (e) {
            setIsLoading(false);
            console.error(e);
            snackbar.error(<FormattedMessage id="applicationProcessingEmailSendError" />);
        }


    }, [emailSender, emailRecipient, emailText, emailSubject, error, isLoading]);
    const handleCancel = useCallback(async () => {

        //clear errors 
        setError({ elcnEmailSender: false, elcnEmailRecipient: false, elcnEmailSubject: false, elcnEmailText: false });

        setEmailSender('');
        setEmailSubject('');
        setEmailText('');
        setEmailSendError(false);
        onClose();

    }, []);

    return (

        <Dialog
            id="sendEmailDialog"
            fullScreen={fullScreen}
            open={isEmailDialogOpen}
            handleButtonClick={handleSendEmail}
            showTitleBarClose={true}
            onClose={onClose}
            classes={classes.mainContent}
            maxWidth="lg"
            spacingOptions={{ responsive: true }}
        >

            <DialogTitle>
                <FormattedMessage id="applicationProcessingEmailTitle" />
            </DialogTitle>
            <DialogContent>
                {isLoading && <div className={classes.loading}><CircularProgress /></div>}
                {!isLoading && <>
                    <Alert
                        alertType="error"
                        className={classes.alert}
                        open={emailSendError}
                        userDismissable={false}
                        text={intl.formatMessage({ id: 'applicationProcessingEmailSendError' })}
                        variant={INLINE_VARIANT}
                    />
                    <div className={classes.contentPadding}>
                        <TextField
                            id={'elcnEmailRecipient'}
                            label={intl.formatMessage({ id: 'applicationProcessingEmailRecipient' })}
                            onChange={e => updateEmailRecipient(e.target.value)}
                            type="text"
                            value={emailRecipient}
                            error={error['elcnEmailRecipient']}
                            helperText={error['elcnEmailRecipient'] ? intl.formatMessage({ id: 'applicationProcessingEmailRecipientError' }) : ''}
                            disabled
                        />
                    </div>
                    <div className={classes.contentPadding}>
                        <Dropdown
                            id={'elcnEmailSender'}
                            label={intl.formatMessage({ id: 'applicationProcessingEmailSender' })}
                            onChange={e => updateEmailSender(e.target.value)}
                            value={emailSender}
                            error={verifiedEmails.length === 0 || error['elcnEmailSender']}
                            helperText={error['elcnEmailSender'] ? intl.formatMessage({ id: 'applicationProcessingEmailError' }) : ''
                                || verifiedEmails.length === 0 ? intl.formatMessage({ id: 'applicationProcessingEmailMissing' }) : ''}
                            required={true}
                        >
                            {verifiedEmails.map((email) => {
                                return (
                                    <DropdownItem key={email.emailAddress} label={email.emailAddress} value={email.emailAddress} />
                                );
                            })}
                        </Dropdown>
                    </div>
                    <div className={classes.contentPadding}>
                        <TextField
                            id={'elcnEmailSubject'}
                            label={intl.formatMessage({ id: 'applicationProcessingEmailSubject' })}
                            onChange={e => updateEmailSubject(e.target.value)}
                            type="text"
                            value={emailSubject}
                            fullWidth
                            required={true}
                            error={error['elcnEmailSubject']}
                            helperText={error['elcnEmailSubject'] ? intl.formatMessage({ id: 'applicationProcessingEmailSubjectError' }) : ''}
                            maxCharacters={CONSTANTS.applicationProcessingEmailSubjectMaxCharacters}
                        />
                    </div>
                    <div className={classes.contentPadding}>
                        <RichTextField
                            className={classes.textEditor}
                            id={'elcnEmailText'}
                            onChange={e => updateEmailText(e)}
                            value={emailText}
                            errors={error['elcnEmailText']}
                            helperText={error['elcnEmailText'] ? intl.formatMessage({ id: 'applicationProcessingEmailTextError' }) : ''}
                            required={true}
                            placeholders={richTextEditorEmailSendPlaceholders.map(it => ({
                                id: it.id,
                                label: intl.formatMessage({ id: it.label })
                            }))}
                            maxCharacters={CONSTANTS.applicationProcessingEmailContentMaxCharacters}
                            placeholder={intl.formatMessage({ id: 'applicationProcessingEmailText' })}
                            requiredErrorTextId='applicationProcessingEmailTextError'
                            toolbarOptions={{
                                bold: true,
                                italic: true,
                                underline: true,
                                link: true,
                                clean: true,
                                formLink: false
                            }}
                        ></RichTextField>
                    </div>
                </>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} disabled={isLoading} color="secondary">
                    <FormattedMessage id="actionCancel" />
                </Button>
                <Button onClick={handleSendEmail} disabled={isLoading} color="primary">
                    <FormattedMessage id='applicationProcessingEmailSend' />
                </Button>
            </DialogActions>

        </Dialog >


    );
};

SendEmailDialog.propTypes = {
    isEmailDialogOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    personalization: PropTypes.object.isRequired,
    fullScreen: PropTypes.bool
};

export default SendEmailDialog;